export enum RequestType {
	SEASONAL = 'SEASONAL',
	ONE_TIME = 'ONE_TIME',
	EXPRESS = 'EXPRESS',
}

export interface Crop {
	id: string;
	name: string;
	image_url: string;
	country_code?: string;
}

export interface Country {
	name: any;
	alpha_two: string;
	alpha_three?: string;
}

export interface Farmer {
	id: string;
	user_id: string;
	first_name?: string;
	last_name?: string;
	full_name: string;
	profile_image_url: string;
	country?: Country;
}

export interface Location {
	address: string;
	latitude: number;
	longitude: number;
}

export interface FarmDetails {
	area: number;
	unit: string;
	location: Location;
}

export interface Language {
	id: string;
	name: string;
}

export interface Attachment {
	id?: string;
	name?: string;
	url?: string;
}

export interface Attachments {
	files: Attachment[];
	images: Attachment[];
}

export interface ServiceDetails {
	name: string;
	price: number;
	duration: string;
	unit: string;
}

export interface Service {
	id?: string;
	name: string;
	price: number;
	delivery_date?: string;
	duration?: string;
	unit?: string;
}

// For detailed offer view
export interface ExpressOffer {
	id: string;
	specialist_id?: string;
	request_type?: string;
	start_date?: string;
	end_date?: string;
	area?: number;
	unit?: string;
	location_name?: string;
	channelId?: string;
	latitude?: number;
	longitude?: number;
	problem_description?: string;
	offer_id?: string | null;
	payment_status?: string;
	request_status?: string;
	request_files?: string[];
	request_id?: string;
	created_at?: string;
	updated_at?: string;
	f100_url?: string;
	crops: Crop[];
	farmer: Farmer;
	services: Service[] | ServiceDetails;
	total_price?: number;
	currency?: string;
	farm_details?: FarmDetails;
	languages?: Language[];
	communication_languages?: Language[];
	comment?: string;
	request_valid_for_days?: string;
	request_valid_for_timestamp?: string;
	service_delivery_days?: string;
	service_delivery_timestamp?: string;
	is_sent?: boolean;
	is_paid?: boolean;
	duration?: number;
	request_active_days?: number;
	attachments?: string[];
}

// For listing view
export interface OfferListItem {
	id: string;
	specialist_id?: string;
	request_type?: string;
	start_date?: string;
	end_date?: string;
	area?: number;
	unit?: string;
	channelId?: string;
	location_name?: string;
	latitude?: number;
	longitude?: number;
	problem_description?: string;
	offer_id?: string | null;
	payment_status?: string;
	request_status?: string;
	request_files?: string[];
	request_id?: string;
	created_at?: string;
	updated_at?: string;
	f100_url?: string;
	sent_date?: string;
	crops: Crop[];
	farmer: Farmer;
	services?: ServiceDetails;
	service?: Service;
	is_paid: boolean;
	is_sent: boolean;
	offer_active_for_days?: string;
	offer_active_for_timestamp?: string;
	request_active_for_days?: string;
	request_active_for_timestamp?: string;
	duration?: number;
	request_active_days?: number;
	currency?: string;
	languages?: Language[];
}

export interface PaginationData {
	total_pages: number;
	current_page: number;
	items_per_page?: number;
	total_items: number;
}

export interface OffersListResponse {
	data: OfferListItem[];
	pagination: PaginationData;
}

export interface APIResponse {
	success: boolean;
	data: {
		totalData: number;
		totalPages: number;
		currentPage: number;
		hasNextPage: boolean;
		data: OfferListItem[];
	};
	error?: string;
}

export type OfferStatus = 'active' | 'archived' | 'completed' | 'pending' | 'unpaid';

export interface SendOfferRequest {
	price: number;
	currency: string;
	message?: string;
	isFree?: boolean;
}

// Adding API response interfaces from services.ts
export interface RequestDetailResponse {
	success: boolean;
	data: {
		id: string;
		farmer_id: string;
		specialist_id: string;
		services_id: string;
		request_type: string;
		start_date: string;
		end_date: string;
		crop_id: string;
		area: number;
		unit: string;
		location_name: string;
		latitude: number;
		longitude: number;
		communication_language: string[];
		problem_description: string;
		specialist_connection_code: string | null;
		offer_id: string | null;
		payment_status: string;
		request_status: string;
		request_files: any[];
		request_id: string;
		created_at: string;
		updated_at: string;
	};
	error: string;
}

export interface UserDataResponse {
	success: boolean;
	data: {
		id: string;
		first_name: string;
		user_id: string;
		last_name: string;
		profile_image: string;
		country: {
			id: string;
			name: string;
			alpha_two: string;
			alpha_three?: string;
		};
		unicode?: {
			alpha_two: string;
			alpha_three?: string;
		};
		// Add other user fields as needed
	};
	error: string;
}

export interface ServiceDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
		price: number;
		currency: string;
		duration: string;
		// Add other service fields as needed
	}[];
	error: string;
}

export interface CropDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
		image_url: string;
		country_code: string;
	}[];
	error: string;
}

export interface LanguageDataResponse {
	success: boolean;
	data: {
		id: string;
		name: string;
	}[];
	error: string;
}

export interface CombinedRequestDetail extends ExpressOffer {
	request: RequestDetailResponse['data'];
	user: UserDataResponse['data'];
	services: ServiceDataResponse['data'];
}
